import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from './styled/Card';
import { PrimaryButton } from './styled/Button';
import { Input, FormGroup, FormLabel, FormError } from './styled/Input';
import { Container, Main, Flex } from './styled/Layout';

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      // In a real app, this would call a registration API
      // For demo purposes, we'll just log them in
      const result = await login(formData.email, formData.password);
      if (result.success) {
        navigate('/');
      } else {
        setError(result.error || 'Registration failed');
      }
    } catch (error) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Main style={{ justifyContent: 'center', alignItems: 'center' }}>
        <Card style={{ maxWidth: '400px', width: '100%' }}>
          <CardHeader>
            <Flex align="center" gap="var(--space-2)" style={{ marginBottom: 'var(--space-3)' }}>
              <span style={{ fontSize: '24px' }}>☁️</span>
              <CardTitle style={{ fontSize: 'var(--font-size-h1)', margin: 0 }}>
                {window.location.hostname.split('.')[0]}
              </CardTitle>
            </Flex>
            <h1 style={{ 
              fontSize: 'var(--font-size-h1)', 
              fontWeight: 'var(--font-weight-bold)', 
              color: 'var(--text-primary)',
              margin: '0 0 var(--space-2) 0'
            }}>
              Create Account
            </h1>
            <p style={{ 
              fontSize: 'var(--font-size-body)', 
              color: 'var(--text-secondary)',
              margin: 0
            }}>
              Join {window.location.hostname.split('.')[0]}
            </p>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit}>
              {error && (
                <FormError style={{ marginBottom: 'var(--space-3)' }}>
                  {error}
                </FormError>
              )}

              <FormGroup>
                <FormLabel htmlFor="name">Full Name</FormLabel>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Enter your full name"
                />
              </FormGroup>

              <FormGroup>
                <FormLabel htmlFor="email">Email Address</FormLabel>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="Enter your email"
                />
              </FormGroup>

              <FormGroup>
                <FormLabel htmlFor="password">Password</FormLabel>
                <div style={{ position: 'relative' }}>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder="Create a password"
                    style={{ paddingRight: '45px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      color: 'var(--text-secondary)',
                      fontSize: '18px',
                      lineHeight: '1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                <p style={{ 
                  fontSize: 'var(--font-size-fine)', 
                  color: 'var(--text-secondary)',
                  margin: 'var(--space-1) 0 0 0'
                }}>
                  Must contain: uppercase, lowercase, number (8+ chars)
                </p>
              </FormGroup>

              <FormGroup>
                <FormLabel htmlFor="confirmPassword">Confirm Password</FormLabel>
                <div style={{ position: 'relative' }}>
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    placeholder="Confirm your password"
                    style={{ paddingRight: '45px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      color: 'var(--text-secondary)',
                      fontSize: '18px',
                      lineHeight: '1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    title={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </FormGroup>

              <PrimaryButton 
                type="submit" 
                style={{ width: '100%', marginTop: 'var(--space-3)' }}
                disabled={loading}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </PrimaryButton>
            </form>
          </CardContent>

          <CardFooter>
            <p style={{ 
              fontSize: 'var(--font-size-body)', 
              color: 'var(--text-secondary)',
              margin: 0,
              textAlign: 'center'
            }}>
              Already have an account? 
              <a 
                href="/login" 
                style={{ 
                  color: 'var(--text-link)', 
                  textDecoration: 'underline',
                  marginLeft: 'var(--space-1)'
                }}
              > 
                Sign in
              </a>
            </p>
          </CardFooter>
        </Card>
      </Main>
    </Container>
  );
};

export default Register;
